import { ETextInputTypes, TextInput } from '@outdoorsyco/bonfire';
import React, { useEffect, useState } from 'react';

interface INumericInputFieldProps {
  label: string;
  value: number;
  minValue?: number;
  maxValue?: number;
  onChange: (value: number) => void;
}

export const NumericInputField = ({
  label,
  value,
  minValue,
  maxValue,
  onChange,
}: INumericInputFieldProps) => {
  // Local text so the field can be cleared and retyped. A field controlled purely
  // by the numeric `value` would snap an emptied field back to its number on the
  // next render, making it impossible to replace (e.g. clear "1" to type "2").
  const [text, setText] = useState<string>(String(value));

  // Re-sync when `value` changes from outside, unless the current text already
  // represents it (so we don't clobber an in-progress edit).
  useEffect(() => {
    setText(current => (Number(current) === value ? current : String(value)));
  }, [value]);

  const clamp = (n: number): number => {
    const lowerBounded = minValue != null ? Math.max(n, minValue) : n;
    return maxValue != null ? Math.min(lowerBounded, maxValue) : lowerBounded;
  };

  const handleChange = (next: string): void => {
    // Allow an empty field while editing — the value is committed on blur.
    if (next === '') {
      setText('');
      return;
    }

    const parsed = Number(next);
    if (Number.isNaN(parsed)) return;

    const clamped = clamp(parsed);
    setText(String(clamped));
    onChange(clamped);
  };

  const handleBlur = (): void => {
    // Leaving the field empty falls back to the minimum (or 0).
    if (text === '') {
      const fallback = minValue ?? 0;
      setText(String(fallback));
      onChange(fallback);
    }
  };

  return (
    <TextInput
      controlled
      type={ETextInputTypes.Number}
      label={label}
      ariaLabel={label}
      inputValue={text}
      min={minValue}
      max={maxValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};
